import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { extractMapSrc } from "../../utils/format";
import { resizeImage } from "../../utils/image";

const MinpakuForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const initialPhotosRef = useRef([]);
  const photoKeyRef = useRef(0);
  const nextPhotoKey = () => `p${photoKeyRef.current++}`;

  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [photos, setPhotos] = useState([]); // [{ key, kind:'existing'|'new', url, file? }]
  const [reservationUrls, setReservationUrls] = useState([{ label: "", url: "" }]);
  const [mapUrl, setMapUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 詳細取得
useEffect(() => {
  if (!isEdit) return;
  const token = localStorage.getItem("admin_jwt");
  fetch(`/api/admin/minpaku?id=${encodeURIComponent(id)}`, {
    headers: {
      "Authorization": `Bearer ${token}`,
    },
  })
    .then((r) => {
      if (!r.ok) throw new Error("取得失敗");
      return r.json();
    })
    .then((data) => {
      setName(data.name || "");
      setBody(data.body || "");
      const loadedPhotos = Array.isArray(data.photos) ? data.photos : [];
      setPhotos(
        loadedPhotos.map((url) => ({ key: nextPhotoKey(), kind: "existing", url }))
      );
      initialPhotosRef.current = loadedPhotos;
      setReservationUrls(
        Array.isArray(data.reservationUrls) && data.reservationUrls.length
          ? data.reservationUrls
          : [{ label: "", url: "" }]
      );
      setMapUrl(typeof data.mapUrl === "string" ? data.mapUrl : "");
    })
    .catch(console.error);
}, [id, isEdit]);

  // 新規ファイル選択（推奨値へ自動リサイズ・圧縮。写真リスト末尾に追加）
  const onSelectFiles = async (e) => {
    const picked = Array.from(e.target.files);
    e.target.value = ""; // 同じファイルを選び直せるように
    const resized = await Promise.all(picked.map((f) => resizeImage(f)));
    setPhotos((prev) => [
      ...prev,
      ...resized.map((f) => ({
        key: nextPhotoKey(),
        kind: "new",
        url: URL.createObjectURL(f),
        file: f,
      })),
    ]);
  };

  // 写真の削除（新規はobjectURLを解放）
  const handleRemovePhoto = (key) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.key === key);
      if (target?.kind === "new" && target.url) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.key !== key);
    });
  };

  // 写真の並び替え（dir = -1:前へ / +1:後ろへ）
  const movePhoto = (index, dir) => {
    setPhotos((prev) => {
      const to = index + dir;
      if (to < 0 || to >= prev.length) return prev;
      const arr = [...prev];
      const [moved] = arr.splice(index, 1);
      arr.splice(to, 0, moved);
      return arr;
    });
  };

  // 保存処理
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // 二重送信ガード
    setIsSubmitting(true);
    const token = localStorage.getItem("admin_jwt");

    try {
    // 写真：並び順を保持して finalPhotos を構築（新規はアップロード）
    const finalPhotos = [];
    for (const p of photos) {
      if (p.kind === "existing") {
        finalPhotos.push(p.url);
        continue;
      }
      const fd = new FormData();
      fd.append("file", p.file);
      const res = await fetch("/api/blob/upload", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        alert("画像アップロードに失敗しました");
        return;
      }
      const json = await res.json();
      finalPhotos.push(json.url);
    }

    // ★ 削除対象画像（編集時のみ）
    if (isEdit) {
      // 「最初に取得した画像リスト - 今回のfinalPhotos」に含まれるものを削除
      const deleted = initialPhotosRef.current.filter((url) => !finalPhotos.includes(url));
      for (let url of deleted) {
        try {
          await fetch("/api/blob/delete", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({ url }),
          });
        } catch (err) {
          console.warn("Blob削除APIエラー:", err);
        }
      }
    }

    // 本体保存API
    const payload = {
      id: isEdit ? id : undefined,
      name,
      body,
      photos: finalPhotos,
      reservationUrls,
      mapUrl: extractMapSrc(mapUrl),
    };

    const apiRes = await fetch("/api/admin/minpaku", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (apiRes.ok) {
      photos.forEach(
        (p) => p.kind === "new" && p.url && URL.revokeObjectURL(p.url)
      );
      alert("保存しました");
      nav("/admin/minpaku");
    } else {
      alert("保存に失敗しました");
    }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="adm-title mb-4">
        {isEdit ? "民泊情報編集" : "民泊情報追加"}
      </h1>
      <form onSubmit={handleSubmit} className="adm-form adm-card space-y-4">
        {/* 物件名 */}
        <div>
          <label>物件名</label>
          <input
            className="adm-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        {/* 紹介文 */}
        <div>
          <label>紹介文</label>
          <textarea
            className="adm-input"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
          />
        </div>
        {/* 写真 */}
        <div>
          <label>写真（複数可・◀▶で並び替え／先頭がメイン）</label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={onSelectFiles}
            className="block mb-2"
          />
          <div className="grid grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <div key={p.key} className="relative border rounded p-1">
                <img
                  src={p.url}
                  alt=""
                  className={`h-24 w-full object-cover ${
                    p.kind === "new" ? "opacity-90" : ""
                  }`}
                />
                {i === 0 && (
                  <span className="absolute top-1 left-1 bg-primary text-white text-[10px] px-1 rounded">
                    メイン
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleRemovePhoto(p.key)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 text-xs flex items-center justify-center"
                >
                  ×
                </button>
                <div className="flex justify-between mt-1">
                  <button
                    type="button"
                    onClick={() => movePhoto(i, -1)}
                    disabled={i === 0}
                    className="px-2 border rounded text-sm disabled:opacity-30"
                    aria-label="前へ移動"
                  >
                    ◀
                  </button>
                  <button
                    type="button"
                    onClick={() => movePhoto(i, 1)}
                    disabled={i === photos.length - 1}
                    className="px-2 border rounded text-sm disabled:opacity-30"
                    aria-label="後ろへ移動"
                  >
                    ▶
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* 予約リンク */}
        <div>
          <label>予約リンク</label>
          {reservationUrls.map((r, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input
                placeholder="サイト名"
                className="border p-1 flex-1"
                value={r.label}
                onChange={(e) =>
                  setReservationUrls((arr) =>
                    arr.map((x, idx) =>
                      idx === i ? { ...x, label: e.target.value } : x
                    )
                  )
                }
              />
              <input
                placeholder="URL"
                className="border p-1 flex-2"
                value={r.url}
                onChange={(e) =>
                  setReservationUrls((arr) =>
                    arr.map((x, idx) =>
                      idx === i ? { ...x, url: e.target.value } : x
                    )
                  )
                }
              />
              <button
                type="button"
                onClick={() =>
                  setReservationUrls((arr) => arr.filter((_, idx) => idx !== i))
                }
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setReservationUrls((arr) => [...arr, { label: "", url: "" }])
            }
            className="text-blue-600"
          >
            ＋行を追加
          </button>
        </div>
        {/* マップ埋め込みURL */}
        <div>
          <label>マップ埋め込みURL</label>
          <input
            className="adm-input"
            value={mapUrl}
            onChange={(e) => setMapUrl(e.target.value)}
            placeholder="https://www.google.com/maps/..."
          />
        </div>
        {/* 保存／キャンセル */}
        <div className="flex gap-2">
          <button type="submit" disabled={isSubmitting} className="adm-btn">
            {isSubmitting ? "保存中…" : "保存"}
          </button>
          <button
            type="button"
            onClick={() => nav("/admin/minpaku")}
            className="adm-btn-sub"
          >
            キャンセル
          </button>
        </div>
      </form>
    </div>
  );
};

export default MinpakuForm;
